import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { CenterFocusStrong, ReportProblem, TaskAlt } from "@mui/icons-material";
import {
  useConfirmValidation,
  useViewVersion,
} from "@/api/hooks/documents";
import type { DocumentValidation } from "@/api/types";
import { DocumentVersionViewer } from "./DocumentVersionViewer";
import {
  HighlightingPdfViewer,
  type Highlight,
  type HighlightKind,
  type HighlightingPdfViewerHandle,
} from "./HighlightingPdfViewer";

interface Props {
  documentId: string;
  versionId: string;
  /** The MIME of the version under review — used by the viewer */
  contentType: string;
  /** Human label for the version (e.g. "v1") shown in the viewer chrome */
  versionLabel: string;
  /** The NEEDS_REVIEW validation row */
  validation: DocumentValidation;
  /** Called with the updated validation after a successful confirm */
  onConfirmed: (updated: DocumentValidation) => void;
  /** Called when the operator backs out of the review */
  onCancel: () => void;
}

interface FormState {
  candidate_name: string;
  completion_date: string; // YYYY-MM-DD
  expiration_date: string; // YYYY-MM-DD or "" for none
}

function readInitial(v: DocumentValidation): FormState {
  const extracted = (v.extracted_value ?? {}) as {
    candidate_name?: string;
    completion_date?: string;
    expiration_date?: string | null;
  };
  return {
    candidate_name: extracted.candidate_name ?? "",
    completion_date: extracted.completion_date ?? "",
    expiration_date: extracted.expiration_date ?? "",
  };
}

function readWarnings(v: DocumentValidation): string[] {
  const extracted = (v.extracted_value ?? {}) as { warnings?: string[] };
  return Array.isArray(extracted.warnings) ? extracted.warnings : [];
}

/**
 * Side-by-side panel: the document on the left, an editable form +
 * confirm action on the right. Wide layout on >= md screens; stacked
 * below that. Used both during initial upload (NEEDS_REVIEW arrives
 * straight from /complete) and later from the document drawer when an
 * operator returns to finish a pending review.
 */
export function GcpCertReviewPanel({
  documentId,
  versionId,
  contentType,
  versionLabel,
  validation,
  onConfirmed,
  onCancel,
}: Props) {
  const view = useViewVersion();
  const confirm = useConfirmValidation(documentId);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [viewerError, setViewerError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(readInitial(validation));
  const [error, setError] = useState<string | null>(null);
  const viewerRef = useRef<HighlightingPdfViewerHandle | null>(null);

  const warnings = readWarnings(validation);
  const isPdf = contentType.toLowerCase().startsWith("application/pdf");

  const highlights: Highlight[] = [];
  if (form.candidate_name) {
    highlights.push({
      key: "name",
      label: "Name",
      value: form.candidate_name,
      isDate: false,
    });
  }
  if (form.completion_date) {
    highlights.push({
      key: "completion_date",
      label: "Completion",
      value: form.completion_date,
      isDate: true,
    });
  }
  if (form.expiration_date) {
    highlights.push({
      key: "expiration_date",
      label: "Expiration",
      value: form.expiration_date,
      isDate: true,
    });
  }

  const scrollTo = (key: HighlightKind) => () => {
    viewerRef.current?.scrollToField(key);
  };

  useEffect(() => {
    // Fetch the presigned URL once when the panel mounts. The view
    // mutation also writes a document_version_viewed audit event, so
    // the trail records that the operator actually opened the doc
    // (not just clicked Confirm blindly).
    let active = true;
    setViewerError(null);
    view
      .mutateAsync({ documentId, versionId })
      .then((res) => {
        if (active) setViewerUrl(res.download_url);
      })
      .catch((e) => {
        if (active)
          setViewerError(e instanceof Error ? e.message : "Could not open viewer");
      });
    return () => {
      active = false;
    };
    // We only want to fetch once on mount — the mutation hook identity
    // changes on every render but that's not a reason to refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, versionId]);

  const expiredWarning = (() => {
    if (!form.expiration_date) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const exp = new Date(form.expiration_date + "T00:00:00");
    if (Number.isNaN(exp.getTime())) return null;
    return exp < today ? `Expired on ${form.expiration_date}` : null;
  })();

  const handleSubmit = async () => {
    setError(null);
    if (!form.candidate_name.trim()) {
      setError("Candidate name is required");
      return;
    }
    if (!form.completion_date) {
      setError("Completion date is required");
      return;
    }
    try {
      const result = await confirm.mutateAsync({
        validationId: validation.id,
        payload: {
          candidate_name: form.candidate_name.trim(),
          completion_date: form.completion_date,
          expiration_date: form.expiration_date || null,
        },
      });
      onConfirmed(result.validation);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Confirm failed");
    }
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 360px" },
        gap: 2,
      }}
    >
      <Box>
        {viewerError && <Alert severity="error">{viewerError}</Alert>}
        {!viewerError && viewerUrl === null && (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {viewerUrl && isPdf && (
          <HighlightingPdfViewer
            ref={viewerRef}
            url={viewerUrl}
            fileName="Certificate"
            versionLabel={versionLabel}
            highlights={highlights}
            onClose={onCancel}
          />
        )}
        {viewerUrl && !isPdf && (
          <DocumentVersionViewer
            url={viewerUrl}
            contentType={contentType}
            fileName="Certificate"
            versionLabel={versionLabel}
            onClose={onCancel}
          />
        )}
      </Box>

      <Stack spacing={2}>
        <Typography variant="overline" color="text.secondary">
          Confirm extracted fields
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Bedrock pulled these values from the certificate. Compare them
          to the document on the left; correct anything that's wrong,
          then hit Confirm. The values you submit are what get persisted
          to the investigator's credential record.
        </Typography>

        {warnings.length > 0 && (
          <Stack spacing={0.5}>
            {warnings.map((w, i) => (
              <Chip
                key={i}
                size="small"
                color="warning"
                icon={<ReportProblem style={{ fontSize: 16 }} />}
                label={w}
                variant="outlined"
                sx={{ justifyContent: "flex-start" }}
              />
            ))}
          </Stack>
        )}
        {expiredWarning && (
          <Chip
            size="small"
            color="error"
            icon={<ReportProblem style={{ fontSize: 16 }} />}
            label={expiredWarning}
            variant="outlined"
            sx={{ justifyContent: "flex-start" }}
          />
        )}

        <TextField
          required
          label="Candidate name"
          value={form.candidate_name}
          onChange={(e) =>
            setForm((f) => ({ ...f, candidate_name: e.target.value }))
          }
          disabled={confirm.isPending}
          slotProps={{
            input: isPdf
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Show on document">
                        <IconButton
                          size="small"
                          onClick={scrollTo("name")}
                          edge="end"
                        >
                          <CenterFocusStrong fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }
              : undefined,
          }}
        />
        <TextField
          required
          type="date"
          label="Completion date"
          slotProps={{
            inputLabel: { shrink: true },
            input: isPdf
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Show on document">
                        <IconButton
                          size="small"
                          onClick={scrollTo("completion_date")}
                          edge="end"
                        >
                          <CenterFocusStrong fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }
              : undefined,
          }}
          value={form.completion_date}
          onChange={(e) =>
            setForm((f) => ({ ...f, completion_date: e.target.value }))
          }
          disabled={confirm.isPending}
        />
        <TextField
          type="date"
          label="Expiration date"
          helperText="Leave blank for certs that don't expire."
          slotProps={{
            inputLabel: { shrink: true },
            input: isPdf
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Show on document">
                        <IconButton
                          size="small"
                          onClick={scrollTo("expiration_date")}
                          edge="end"
                        >
                          <CenterFocusStrong fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }
              : undefined,
          }}
          value={form.expiration_date}
          onChange={(e) =>
            setForm((f) => ({ ...f, expiration_date: e.target.value }))
          }
          disabled={confirm.isPending}
        />

        {error && <Alert severity="error">{error}</Alert>}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button onClick={onCancel} disabled={confirm.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={confirm.isPending}
            startIcon={
              confirm.isPending ? (
                <CircularProgress size={14} />
              ) : (
                <TaskAlt fontSize="small" />
              )
            }
          >
            {confirm.isPending ? "Saving…" : "Confirm"}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
